import React, { memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Server, AlertTriangle, CheckCircle, Cpu, HardDrive, Activity } from 'lucide-react';
import { services, systemTechInfos, vmDetails } from '../data/mockData';
import { Button } from '../components/ui/Button';
import { ChartCard } from '../components/ui/ChartCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
export function SystemDetails() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const service = services.find(s => s.uuid === id);
  const techInfo = id ? systemTechInfos[id] : null;
  if (!service || !techInfo) {
    return <div className="p-6" data-id="element-526">System not found</div>;
  }
  // Mock graph data
  const performanceData = [{
    time: '00:00',
    cpu: 45,
    memory: 60
  }, {
    time: '04:00',
    cpu: 55,
    memory: 65
  }, {
    time: '08:00',
    cpu: 75,
    memory: 80
  }, {
    time: '12:00',
    cpu: 85,
    memory: 85
  }, {
    time: '16:00',
    cpu: 70,
    memory: 75
  }, {
    time: '20:00',
    cpu: 50,
    memory: 60
  }];
  return <div className="space-y-6" data-id="element-527">
      <div className="flex items-center space-x-4 mb-6" data-id="element-528">
        <Button variant="ghost" onClick={() => navigate('/technical')} className="p-2" data-id="element-529">
          <ArrowLeft className="w-5 h-5" data-id="element-530" />
        </Button>
        <div data-id="element-531">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-532">
            {service.name_en} Dashboard
          </h1>
          <p className="text-slate-500" data-id="element-533">System Details & Performance Metrics</p>
        </div>
      </div>

      {/* Alerts Section */}
      {techInfo.alerts.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-id="element-534">
          <h3 className="text-red-800 font-semibold flex items-center mb-2" data-id="element-535">
            <AlertTriangle className="w-5 h-5 mr-2" data-id="element-536" /> Critical Alerts
          </h3>
          <div className="space-y-2" data-id="element-537">
            {techInfo.alerts.map(alert => <div key={alert.id} className="flex items-center text-sm text-red-700 bg-white/50 p-2 rounded" data-id="element-538">
                <span className="font-medium mr-2" data-id="element-539">
                  [{new Date(alert.timestamp).toLocaleTimeString()}]
                </span>
                {alert.message}
              </div>)}
          </div>
        </div>}

      {/* Performance Graph */}
      <ChartCard title="System Performance (Last 24h)" data-id="element-540">
        <div className="h-80" data-id="element-541">
          <ResponsiveContainer width="100%" height="100%" data-id="element-542">
            <LineChart data={performanceData} data-id="element-543">
              <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-544" />
              <XAxis dataKey="time" data-id="element-545" />
              <YAxis data-id="element-546" />
              <Tooltip data-id="element-547" />
              <Line type="monotone" dataKey="cpu" stroke="#059669" name="CPU %" strokeWidth={2} data-id="element-548" />
              <Line type="monotone" dataKey="memory" stroke="#3b82f6" name="Memory %" strokeWidth={2} data-id="element-549" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* VM List */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-id="element-550">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center" data-id="element-551">
          <h3 className="font-semibold text-slate-800" data-id="element-552">
            Virtual Machines Status
          </h3>
          <span className="text-sm text-slate-500" data-id="element-553">
            {techInfo.totalRunning} / {techInfo.totalVm} Running
          </span>
        </div>
        <div className="overflow-x-auto" data-id="element-554">
          <table className="w-full text-sm text-left" data-id="element-555">
            <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-556">
              <tr data-id="element-557">
                <th className="px-6 py-3" data-id="element-558">VM Name</th>
                <th className="px-6 py-3" data-id="element-559">Status</th>
                <th className="px-6 py-3" data-id="element-560">CPU</th>
                <th className="px-6 py-3" data-id="element-561">Memory</th>
                <th className="px-6 py-3" data-id="element-562">Disk</th>
                <th className="px-6 py-3" data-id="element-563">Uptime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-564">
              {vmDetails.map(vm => <tr key={vm.id} className="hover:bg-slate-50" data-id="element-565">
                  <td className="px-6 py-4 font-medium text-slate-900 flex items-center" data-id="element-566">
                    <Server className="w-4 h-4 mr-2 text-slate-400" data-id="element-567" />
                    {vm.name}
                  </td>
                  <td className="px-6 py-4" data-id="element-568">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${vm.status === 'running' ? 'bg-green-100 text-green-800' : vm.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-800'}`} data-id="element-569">
                      {vm.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-570">
                    <div className="flex items-center" data-id="element-571">
                      <div className="w-16 bg-slate-200 rounded-full h-1.5 mr-2" data-id="element-572">
                        <div className="bg-emerald-500 h-1.5 rounded-full" style={{
                      width: `${vm.cpu}%`
                    }} data-id="element-573"></div>
                      </div>
                      {vm.cpu}%
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-574">
                    <div className="flex items-center" data-id="element-575">
                      <div className="w-16 bg-slate-200 rounded-full h-1.5 mr-2" data-id="element-576">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{
                      width: `${vm.memory}%`
                    }} data-id="element-577"></div>
                      </div>
                      {vm.memory}%
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-578">{vm.disk}%</td>
                  <td className="px-6 py-4 text-slate-500" data-id="element-579">{vm.uptime}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}